import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="p-4 bg-gray-950 text-gray-100 text-sm overflow-x-auto font-mono leading-relaxed">
        <code>{children.trim()}</code>
      </pre>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

function NodeBadge({ color, children }: { color: string; children: React.ReactNode }) {
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    violet: 'bg-violet-500/10 text-violet-400 border-violet-500/30',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
    cyan: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30',
    rose: 'bg-rose-500/10 text-rose-400 border-rose-500/30',
    slate: 'bg-gray-500/10 text-gray-400 border-gray-500/30',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-mono font-medium ${colorMap[color] || colorMap.slate}`}>
      {children}
    </span>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'node-categories', label: 'Node Categories' },
  { id: 'trigger-nodes', label: 'Trigger Nodes' },
  { id: 'extract-nodes', label: 'Extract Nodes' },
  { id: 'http-request', label: 'HTTP Request', level: 1 },
  { id: 'splunk-search', label: 'Splunk Search', level: 1 },
  { id: 'authentication', label: 'Authentication', level: 1 },
  { id: 'transform-nodes', label: 'Transform Nodes' },
  { id: 'transform', label: 'Transform', level: 1 },
  { id: 'field-operations', label: 'Field Operations', level: 1 },
  { id: 'json-csv', label: 'JSON & CSV', level: 1 },
  { id: 'redact-mask', label: 'Redact / Mask', level: 1 },
  { id: 'schema-mapping', label: 'Schema Mapping', level: 1 },
  { id: 'lookup', label: 'Lookup', level: 1 },
  { id: 'flow-control', label: 'Flow Control' },
  { id: 'condition', label: 'Condition', level: 1 },
  { id: 'split-join', label: 'Split & Join', level: 1 },
  { id: 'load-nodes', label: 'Load Nodes' },
  { id: 'logpulse-ingest', label: 'LogPulse Ingest', level: 1 },
  { id: 'utility-nodes', label: 'Utility Nodes' },
  { id: 'dynamic-values', label: 'Dynamic Values' },
  { id: 'context-store', label: 'Context Store' },
  { id: 'pipeline-context', label: 'Pipeline Context', level: 1 },
  { id: 'tenant-context', label: 'Tenant Context', level: 1 },
  { id: 'context-patterns', label: 'Common Patterns', level: 1 },
  { id: 'pipeline-settings', label: 'Pipeline Settings' },
  { id: 'trigger-modes', label: 'Trigger Modes', level: 1 },
  { id: 'test-events', label: 'Test Events', level: 1 },
  { id: 'execution-tracking', label: 'Execution & Tracking' },
  { id: 'run-status', label: 'Run Status', level: 1 },
  { id: 'node-execution', label: 'Node Execution Details', level: 1 },
  { id: 'realtime-updates', label: 'Real-Time Updates', level: 1 },
  { id: 'versioning', label: 'Versioning' },
  { id: 'api-reference', label: 'API Reference' },
  { id: 'pipeline-endpoints', label: 'Pipeline Endpoints', level: 1 },
  { id: 'execution-endpoints', label: 'Execution Endpoints', level: 1 },
  { id: 'version-endpoints', label: 'Version Endpoints', level: 1 },
  { id: 'test-event-endpoints', label: 'Test Event Endpoints', level: 1 },
];

export function BuildingPipelinesPage() {
  useDocumentMeta({ title: 'Building Pipelines: LogPulse Docs', description: 'Design visual pipelines with drag-and-drop nodes for log transformation, filtering, and routing.' });

  const t = useT();

  return (
    <div className="flex gap-8">
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            {t('docs.pipelines.title')}
          </h1>
          <P>{t('docs.pipelines.intro')}</P>
        </div>

        {/* ---- Overview ---- */}
        <Section id="overview" title={t('docs.pipelines.overview.title')}>
          <P>{t('docs.pipelines.overview.description')}</P>

          <div className="grid sm:grid-cols-2 gap-4 my-6">
            {(['visual', 'nodeTypes', 'realtime', 'versioned'] as const).map((key) => (
              <div key={key} className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
                <h4 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
                  {t(`docs.pipelines.overview.${key}Title`)}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t(`docs.pipelines.overview.${key}Desc`)}
                </p>
              </div>
            ))}
          </div>
        </Section>

        {/* ---- Getting Started ---- */}
        <Section id="getting-started" title={t('docs.pipelines.gettingStarted.title')}>
          <P>{t('docs.pipelines.gettingStarted.description')}</P>

          <div className="my-6 space-y-4">
            {[1, 2, 3, 4, 5].map((step) => (
              <div key={step} className="flex gap-4 items-start">
                <div className="w-7 h-7 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-primary-600 dark:text-primary-400">{step}</span>
                </div>
                <div>
                  <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                    {t(`docs.pipelines.gettingStarted.step${step}Title`)}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {t(`docs.pipelines.gettingStarted.step${step}Desc`)}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <Callout type="tip">{t('docs.pipelines.gettingStarted.tip')}</Callout>
        </Section>

        {/* ---- Node Categories ---- */}
        <Section id="node-categories" title={t('docs.pipelines.nodeCategories.title')}>
          <P>{t('docs.pipelines.nodeCategories.description')}</P>

          <Table
            headers={['Category', 'Color', 'Node Types', 'Description']}
            rows={[
              ['Trigger', 'Emerald', 'start', 'Entry point for pipeline execution'],
              ['Extract', 'Blue', 'httpRequest, splunkSearch', 'Pull data from external sources'],
              ['Transform', 'Violet', 'transform, fieldOperations, json, csv, redactMask, mapCommonSchema, lookup', 'Modify, parse, and enrich data'],
              ['Flow Control', 'Amber', 'condition, split, join', 'Control execution flow with branching and fan-out / re-merge'],
              ['Load', 'Cyan', 'logpulseIngest', 'Send data to LogPulse or external destinations'],
              ['Utility', 'Slate', 'log, error', 'Debug logging and error handling'],
            ]}
          />

          <P>{t('docs.pipelines.nodeCategories.dragDrop')}</P>
        </Section>

        {/* ---- Trigger Nodes ---- */}
        <Section id="trigger-nodes" title={t('docs.pipelines.trigger.title')}>
          <P>{t('docs.pipelines.trigger.description')}</P>

          <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 p-4">
            <div className="flex items-center gap-2 mb-2">
              <NodeBadge color="emerald">start</NodeBadge>
              <span className="text-sm font-medium text-gray-900 dark:text-white">Start Node</span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('docs.pipelines.trigger.startDesc')}
            </p>
          </div>

          <P>{t('docs.pipelines.trigger.modes')}</P>

          <Table
            headers={['Mode', 'Description', 'Configuration']}
            rows={[
              ['Scheduled', 'Runs on a cron schedule', 'Cron expression + timezone (e.g., */5 * * * * UTC)'],
              ['HTTP Ingestion', 'Triggered by incoming API key events', 'Link pipeline to an API key'],
              ['Manual', 'Run on demand via UI or API', 'No configuration needed'],
            ]}
          />

          <Callout type="info">{t('docs.pipelines.trigger.note')}</Callout>
        </Section>

        {/* ---- Extract Nodes ---- */}
        <Section id="extract-nodes" title={t('docs.pipelines.extract.title')}>
          <P>{t('docs.pipelines.extract.description')}</P>

          {/* HTTP Request */}
          <SubSection id="http-request" title={t('docs.pipelines.extract.http.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="blue">httpRequest</NodeBadge>
            </div>
            <P>{t('docs.pipelines.extract.http.description')}</P>

            <Table
              headers={['Setting', 'Type', 'Description']}
              rows={[
                ['URL', 'DynamicValue', 'Target URL for the request'],
                ['Method', 'DynamicValue', 'GET, POST, PUT, PATCH, or DELETE'],
                ['Headers', 'Key-Value pairs', 'Custom request headers (DynamicValue per header)'],
                ['Query Params', 'Key-Value pairs', 'URL query parameters (DynamicValue per param)'],
                ['Body', 'DynamicValue', 'Request body (for POST/PUT/PATCH)'],
                ['Timeout', 'Number', 'Request timeout in ms (default: 30000)'],
                ['Retries', 'Number', 'Number of retry attempts on failure'],
                ['Retry Delay', 'Number', 'Delay between retries in ms'],
                ['Follow Redirects', 'Boolean', 'Follow HTTP redirects (default: true)'],
                ['Max Redirects', 'Number', 'Maximum redirects to follow (default: 5)'],
                ['Validate SSL', 'Boolean', 'Validate SSL certificates (default: true)'],
                ['Output Path', 'String', 'Where to store the response in the payload'],
              ]}
            />

            <Callout type="warning">{t('docs.pipelines.extract.http.ssrf')}</Callout>
          </SubSection>

          {/* Splunk Search */}
          <SubSection id="splunk-search" title={t('docs.pipelines.extract.splunk.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="blue">splunkSearch</NodeBadge>
            </div>
            <P>{t('docs.pipelines.extract.splunk.description')}</P>

            <Table
              headers={['Setting', 'Type', 'Description']}
              rows={[
                ['Host', 'DynamicValue', 'Splunk server hostname'],
                ['Port', 'DynamicValue', 'Splunk management port (default: 8089)'],
                ['Protocol', 'https | http', 'Connection protocol (default: https)'],
                ['Auth Type', 'basic | token', 'Authentication method'],
                ['Query', 'DynamicValue', 'SPL search query string'],
                ['Earliest Time', 'String', 'Search time range start (e.g., -24h)'],
                ['Latest Time', 'String', 'Search time range end (e.g., now)'],
                ['Max Results', 'Number', 'Maximum result count (default: 1000)'],
                ['Timeout', 'Number', 'Search timeout in ms'],
                ['Output Path', 'String', 'Where to store search results'],
              ]}
            />
          </SubSection>

          {/* Authentication */}
          <SubSection id="authentication" title={t('docs.pipelines.extract.auth.title')}>
            <P>{t('docs.pipelines.extract.auth.description')}</P>

            <Table
              headers={['Auth Type', 'Fields', 'Description']}
              rows={[
                ['None', '—', 'No authentication'],
                ['Basic', 'Username, Password', 'HTTP Basic authentication (Base64 encoded)'],
                ['Bearer', 'Token', 'Bearer token in Authorization header'],
                ['API Key', 'Header Name, Value', 'Custom header with API key (default: X-API-Key)'],
                ['Multi-Step', 'Steps[], Token Rules', 'Sequential auth flow for OAuth/complex scenarios'],
              ]}
            />

            <P>{t('docs.pipelines.extract.auth.multiStep')}</P>

            <CodeBlock title="Multi-Step Auth Example">
{`Step 1: POST /auth/login
  Body: { "username": "{{envVar:API_USER}}", "password": "{{envVar:API_PASS}}" }
  Extract: body → $.access_token → accessToken

Step 2: GET /api/data
  Header: Authorization = Bearer {{accessToken}}
  Extract: body → $.results → results`}
            </CodeBlock>

            <P>{t('docs.pipelines.extract.auth.extraction')}</P>

            <Table
              headers={['Source', 'Description', 'Example']}
              rows={[
                ['body', 'Extract from JSON response body', '$.access_token'],
                ['header', 'Extract from response header', 'X-Session-Id'],
                ['cookie', 'Extract from response cookie', 'session_cookie'],
              ]}
            />
          </SubSection>
        </Section>

        {/* ---- Transform Nodes ---- */}
        <Section id="transform-nodes" title={t('docs.pipelines.transformNodes.title')}>
          <P>{t('docs.pipelines.transformNodes.description')}</P>

          {/* Transform */}
          <SubSection id="transform" title={t('docs.pipelines.transformNodes.transform.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="violet">transform</NodeBadge>
            </div>
            <P>{t('docs.pipelines.transformNodes.transform.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Simple Mode</h4>
            <P>{t('docs.pipelines.transformNodes.transform.simpleDesc')}</P>

            <Table
              headers={['Value Type', 'Description', 'Example']}
              rows={[
                ['string', 'Static string value', '"production"'],
                ['number', 'Numeric value', '42'],
                ['boolean', 'True or false', 'true'],
                ['expression', 'Reference payload data', 'payload.user.name'],
              ]}
            />

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Advanced Mode (JavaScript)</h4>
            <P>{t('docs.pipelines.transformNodes.transform.advancedDesc')}</P>

            <CodeBlock title="JavaScript Transform Example">
{`// Variables: data, payload, $ (all reference input payload)
const enriched = {
  ...data,
  processed: true,
  timestamp: new Date().toISOString(),
  severity: data.level === 'error' ? 'high' : 'normal',
  tags: [...(data.tags || []), 'processed']
};
return enriched;`}
            </CodeBlock>
          </SubSection>

          {/* Field Operations */}
          <SubSection id="field-operations" title={t('docs.pipelines.transformNodes.fieldOps.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="violet">fieldOperations</NodeBadge>
            </div>
            <P>{t('docs.pipelines.transformNodes.fieldOps.description')}</P>

            <Table
              headers={['Operation', 'Fields', 'Description']}
              rows={[
                ['add', 'field, value, valueType', 'Add a new field with a static or dynamic value'],
                ['remove', 'field', 'Remove a field from the payload'],
                ['rename', 'field, newName', 'Rename a field'],
                ['keep', 'fields[]', 'Keep only the specified fields, remove all others'],
                ['coerce', 'field, targetType', 'Convert field to string, number, boolean, or array'],
              ]}
            />

            <CodeBlock title="Field Operations Example">
{`Operation 1: add    → field: "env", value: "production", type: string
Operation 2: rename → field: "msg", newName: "message"
Operation 3: remove → field: "debug_info"
Operation 4: coerce → field: "status_code", targetType: number`}
            </CodeBlock>
          </SubSection>

          {/* JSON & CSV */}
          <SubSection id="json-csv" title={t('docs.pipelines.transformNodes.jsonCsv.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="blue">json</NodeBadge>
              <NodeBadge color="cyan">csv</NodeBadge>
            </div>
            <P>{t('docs.pipelines.transformNodes.jsonCsv.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">JSON Node</h4>
            <Table
              headers={['Mode', 'Description']}
              rows={[
                ['parse', 'Convert a JSON string to an object. Specify input path (source string) and output path.'],
                ['stringify', 'Convert an object to a JSON string. Specify input path (source object) and output path.'],
              ]}
            />

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">CSV Node</h4>
            <Table
              headers={['Setting', 'Description']}
              rows={[
                ['Mode', 'parse (CSV → array of objects) or format (objects → CSV string)'],
                ['Delimiter', 'Field separator character (default: comma)'],
                ['Has Headers', 'First row contains field names (default: true)'],
                ['Input Path', 'Path to the CSV string or array in payload'],
                ['Output Path', 'Where to store the parsed/formatted result'],
              ]}
            />
          </SubSection>

          {/* Redact / Mask */}
          <SubSection id="redact-mask" title={t('docs.pipelines.transformNodes.redact.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="rose">redactMask</NodeBadge>
            </div>
            <P>{t('docs.pipelines.transformNodes.redact.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">{t('docs.pipelines.transformNodes.redact.presets')}</h4>
            <Table
              headers={['Pattern', 'Default Action', 'Example']}
              rows={[
                ['Email', 'Replace → [EMAIL]', 'user@example.com → [EMAIL]'],
                ['Phone', 'Replace → [PHONE]', '+1-555-0123 → [PHONE]'],
                ['Credit Card', 'Mask (keep last 4)', '4111-1111-1111-1234 → ****-****-****-1234'],
                ['SSN', 'Replace → ***-**-****', '123-45-6789 → ***-**-****'],
                ['IP Address', 'Replace → [IP]', '192.168.1.1 → [IP]'],
                ['JWT Token', 'Replace → [JWT]', 'eyJhbGciOi... → [JWT]'],
                ['API Key', 'Replace → [API_KEY]', 'sk-abc123... → [API_KEY]'],
                ['IBAN', 'Mask (keep first 4)', 'NL91ABNA0417164300 → NL91**************'],
              ]}
            />

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">{t('docs.pipelines.transformNodes.redact.actions')}</h4>
            <Table
              headers={['Action', 'Description', 'Options']}
              rows={[
                ['mask', 'Replace characters with mask character', 'maskChar (default: *), style: fixed / keepLastN / keepFirstN, keepN'],
                ['hash', 'SHA-256 hash of matched value', 'Irreversible, consistent hash'],
                ['remove', 'Delete the field entirely', '—'],
                ['replace', 'Replace with custom text', 'Custom replacement string'],
              ]}
            />

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">{t('docs.pipelines.transformNodes.redact.customRules')}</h4>
            <P>{t('docs.pipelines.transformNodes.redact.customDesc')}</P>

            <Table
              headers={['Match Type', 'Description', 'Example']}
              rows={[
                ['regex', 'Regular expression pattern matching', '/\\b[A-Z]{2}\\d{6}\\b/ (passport numbers)'],
                ['fieldPath', 'Match specific field paths', 'user.email, request.headers.authorization'],
              ]}
            />

            <Callout type="tip">{t('docs.pipelines.transformNodes.redact.auditTip')}</Callout>
          </SubSection>

          {/* Schema Mapping */}
          <SubSection id="schema-mapping" title={t('docs.pipelines.transformNodes.schema.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="violet">mapCommonSchema</NodeBadge>
            </div>
            <P>{t('docs.pipelines.transformNodes.schema.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">{t('docs.pipelines.transformNodes.schema.templates')}</h4>
            <Table
              headers={['Template', 'Description']}
              rows={[
                ['ECS', 'Elastic Common Schema: standard field naming for Elasticsearch/OpenSearch'],
                ['CIM', 'Common Information Model: normalized event field schema'],
                ['OTEL', 'OpenTelemetry: vendor-neutral observability standard'],
                ['LogPulse', 'Native LogPulse data models: custom models defined in your organization'],
                ['Custom', 'User-defined mappings without a base template'],
              ]}
            />

            <P>{t('docs.pipelines.transformNodes.schema.modes')}</P>

            <Table
              headers={['Mode', 'Description']}
              rows={[
                ['Permissive', 'Missing source fields are skipped. Partial output is produced without errors.'],
                ['Strict', 'All required fields must be present. Pipeline errors if required fields are missing.'],
              ]}
            />

            <P>{t('docs.pipelines.transformNodes.schema.coercion')}</P>

            <Table
              headers={['Type', 'Description']}
              rows={[
                ['auto', 'Automatic type detection based on value'],
                ['string', 'Convert to string'],
                ['int', 'Convert to integer'],
                ['float', 'Convert to floating-point number'],
                ['boolean', 'Convert to boolean'],
              ]}
            />

            <Callout type="info">{t('docs.pipelines.transformNodes.schema.keepOriginal')}</Callout>
          </SubSection>

          {/* Lookup */}
          <SubSection id="lookup" title={t('docs.pipelines.transformNodes.lookup.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="cyan">lookup</NodeBadge>
            </div>
            <P>{t('docs.pipelines.transformNodes.lookup.description')}</P>

            <Table
              headers={['Setting', 'Description']}
              rows={[
                ['Dataset', 'Select a lookup dataset from your organization'],
                ['Input Key Path', 'Dot-notation path to the lookup key in your payload'],
                ['Lookup Key Field', 'Field name in the dataset to match against'],
                ['Output Mode', 'How to output matched data (see below)'],
                ['Field Mappings', 'Optional: map specific lookup fields to event fields'],
              ]}
            />

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Output Modes</h4>
            <Table
              headers={['Mode', 'Description']}
              rows={[
                ['mergeIntoEvent', 'Merge all lookup fields into the event payload'],
                ['writeToTargetPath', 'Write lookup data to a specific path in the payload'],
                ['embedLookup', 'Embed the full lookup record as a nested object'],
              ]}
            />
          </SubSection>
        </Section>

        {/* ---- Flow Control ---- */}
        <Section id="flow-control" title={t('docs.pipelines.flowControl.title')}>
          <P>{t('docs.pipelines.flowControl.description')}</P>

          {/* Condition */}
          <SubSection id="condition" title={t('docs.pipelines.flowControl.condition.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="amber">condition</NodeBadge>
            </div>
            <P>{t('docs.pipelines.flowControl.condition.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Operators</h4>
            <Table
              headers={['Operator', 'Symbol', 'Description', 'Value Required']}
              rows={[
                ['eq', '==', 'Equals', 'Yes'],
                ['neq', '!=', 'Not equals', 'Yes'],
                ['gt', '>', 'Greater than', 'Yes'],
                ['lt', '<', 'Less than', 'Yes'],
                ['gte', '>=', 'Greater than or equal', 'Yes'],
                ['lte', '<=', 'Less than or equal', 'Yes'],
                ['contains', 'contains', 'Contains substring', 'Yes'],
                ['not_contains', 'not contains', 'Does not contain', 'Yes'],
                ['regex', 'regex', 'Matches regular expression', 'Yes'],
                ['exists', 'exists', 'Field exists in payload', 'No'],
                ['not_exists', 'not exists', 'Field does not exist', 'No'],
              ]}
            />

            <P>{t('docs.pipelines.flowControl.condition.multiBranch')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Advanced Mode (JavaScript)</h4>
            <P>{t('docs.pipelines.flowControl.condition.advancedDesc')}</P>

            <CodeBlock title="JavaScript Condition Example">
{`// Must return an array of branch IDs or a single string
if (data.level === 'error' && data.status_code >= 500) {
  return ['critical-errors'];
} else if (data.level === 'error') {
  return ['errors'];
}
return ['default'];`}
            </CodeBlock>
          </SubSection>

          {/* Split & Join */}
          <SubSection id="split-join" title={t('docs.pipelines.flowControl.splitJoin.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="amber">split</NodeBadge>
              <NodeBadge color="amber">join</NodeBadge>
            </div>
            <P>{t('docs.pipelines.flowControl.splitJoin.description')}</P>

            <Table
              headers={['Split Setting', 'Description']}
              rows={[
                ['Array Path', 'Dot-notation path to the array to fan out (e.g., payload.items)'],
                ['Output Path', 'Where each element lands inside the per-fork message (default: payload)'],
                ['Rate Limit', 'Optional throttle: release N forks per second instead of all at once'],
                ['Continue on Error', 'Drop failing forks (any matching join will then time out)'],
              ]}
            />

            <Table
              headers={['Join Setting', 'Description']}
              rows={[
                ['Mode', '"auto" matches an upstream split via parts.id; "manual" waits for a fixed count'],
                ['Output Path', 'Where the joined array is placed (default: payload)'],
                ['Count', 'Required in manual mode: number of messages to collect'],
                ['Timeout (ms)', 'Maximum wait for missing parts before failing the join'],
              ]}
            />

            <P>{t('docs.pipelines.flowControl.splitJoin.nesting')}</P>

            <Callout type="tip">{t('docs.pipelines.flowControl.splitJoin.tip')}</Callout>
          </SubSection>
        </Section>

        {/* ---- Load Nodes ---- */}
        <Section id="load-nodes" title={t('docs.pipelines.load.title')}>
          <P>{t('docs.pipelines.load.description')}</P>

          <SubSection id="logpulse-ingest" title={t('docs.pipelines.load.ingest.title')}>
            <div className="flex items-center gap-2 mb-3">
              <NodeBadge color="cyan">logpulseIngest</NodeBadge>
            </div>
            <P>{t('docs.pipelines.load.ingest.description')}</P>

            <Table
              headers={['Setting', 'Default', 'Description']}
              rows={[
                ['Source Type', 'etl_pipeline', 'Value for the sourcetype field in LogPulse'],
                ['Index', 'main', 'Target index for ingested events'],
                ['Host', '—', 'Host name to tag events with'],
                ['Source', 'Auto-generated', 'Source identifier (defaults to pipeline name)'],
                ['Message Path', 'payload', 'Path to the event message/body in payload'],
                ['Timestamp Path', '—', 'Path to timestamp field (auto-detected if empty)'],
                ['Level Path', '—', 'Path to log level field (auto-detected if empty)'],
                ['Field Mapping', '—', 'Optional custom field name mappings'],
              ]}
            />
          </SubSection>
        </Section>

        {/* ---- Utility Nodes ---- */}
        <Section id="utility-nodes" title={t('docs.pipelines.utility.title')}>
          <P>{t('docs.pipelines.utility.description')}</P>

          <div className="grid sm:grid-cols-2 gap-4 my-4">
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center gap-2 mb-2">
                <NodeBadge color="slate">log</NodeBadge>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('docs.pipelines.utility.logDesc')}
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4">
              <div className="flex items-center gap-2 mb-2">
                <NodeBadge color="rose">error</NodeBadge>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('docs.pipelines.utility.errorDesc')}
              </p>
            </div>
          </div>
        </Section>

        {/* ---- Dynamic Values ---- */}
        <Section id="dynamic-values" title={t('docs.pipelines.dynamicValues.title')}>
          <P>{t('docs.pipelines.dynamicValues.description')}</P>

          <Table
            headers={['Type', 'Syntax', 'Description', 'Example']}
            rows={[
              ['static', 'Plain text', 'Literal string or number', '"https://api.example.com"'],
              ['flowVariable', 'Select variable', 'Reference a pipeline variable', 'vars.apiUrl'],
              ['envVariable', '{{envVar:NAME}}', 'Reference an environment variable', '{{envVar:API_KEY}}'],
              ['expression', 'Dot-notation', 'Reference payload data dynamically', 'payload.user.id'],
            ]}
          />

          <Callout type="info">{t('docs.pipelines.dynamicValues.tip')}</Callout>
        </Section>

        {/* ---- Context Store ---- */}
        <Section id="context-store" title={t('docs.pipelines.contextStore.title')}>
          <P>{t('docs.pipelines.contextStore.description')}</P>

          <Table
            headers={['Scope', 'Visibility', 'Typical use']}
            rows={[
              ['pipelineContext', 'Private to one pipeline', 'Polling cursors, dedup caches, last-seen IDs'],
              ['tenantContext', 'Shared across every pipeline in the org', 'Cross-pipeline coordination, shared lookup lists'],
            ]}
          />

          <Callout type="info">{t('docs.pipelines.contextStore.lifecycleTip')}</Callout>

          <SubSection id="pipeline-context" title={t('docs.pipelines.contextStore.pipelineContext.title')}>
            <P>{t('docs.pipelines.contextStore.pipelineContext.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Reading a value</h4>
            <P>{t('docs.pipelines.contextStore.pipelineContext.read')}</P>
            <CodeBlock title="Read pipelineContext into the payload">
{`Operation: set
  target: payload.lastSeenId
  value:
    type: pipelineContext
    value: cursor.lastSeenId`}
            </CodeBlock>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Writing a value</h4>
            <P>{t('docs.pipelines.contextStore.pipelineContext.write')}</P>
            <CodeBlock title="Persist a cursor for the next run">
{`Operation: set
  scope: pipelineContext
  target: cursor.lastSeenId
  value:
    type: jsonata
    value: $max(payload.id)`}
            </CodeBlock>
          </SubSection>

          <SubSection id="tenant-context" title={t('docs.pipelines.contextStore.tenantContext.title')}>
            <P>{t('docs.pipelines.contextStore.tenantContext.description')}</P>
            <CodeBlock title="Producer pipeline writes a shared list">
{`Operation: set
  scope: tenantContext
  target: active_customers
  value:
    type: jsonata
    value: payload.customers`}
            </CodeBlock>
            <CodeBlock title="Consumer pipeline reads it">
{`Operation: set
  target: payload.customers
  value:
    type: tenantContext
    value: active_customers`}
            </CodeBlock>
          </SubSection>

          <SubSection id="context-patterns" title={t('docs.pipelines.contextStore.patterns.title')}>
            <P>{t('docs.pipelines.contextStore.patterns.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Incremental polling cursor</h4>
            <P>{t('docs.pipelines.contextStore.patterns.cursor')}</P>
            <CodeBlock title="Read cursor with first-run fallback">
{`Operation: set
  target: payload.since
  value:
    type: jsonata
    value: $exists($context.cursor.lastPolledAt)
             ? $context.cursor.lastPolledAt
             : "1970-01-01T00:00:00Z"`}
            </CodeBlock>
            <CodeBlock title="Advance cursor after a successful ingest">
{`Operation: set
  scope: pipelineContext
  target: cursor.lastPolledAt
  value:
    type: jsonata
    value: $max(payload.timestamp)`}
            </CodeBlock>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Dedup cache</h4>
            <P>{t('docs.pipelines.contextStore.patterns.dedup')}</P>
            <CodeBlock title="Append seen IDs to a cache array">
{`Operation: set
  scope: pipelineContext
  target: seenIds
  value:
    type: jsonata
    value: $append($context.seenIds, payload.id)`}
            </CodeBlock>

            <Callout type="warning">{t('docs.pipelines.contextStore.patterns.notFor')}</Callout>
          </SubSection>
        </Section>

        {/* ---- Pipeline Settings ---- */}
        <Section id="pipeline-settings" title={t('docs.pipelines.settings.title')}>
          <P>{t('docs.pipelines.settings.description')}</P>

          {/* Trigger Modes */}
          <SubSection id="trigger-modes" title={t('docs.pipelines.settings.triggerModes.title')}>
            <P>{t('docs.pipelines.settings.triggerModes.description')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Scheduled Execution</h4>
            <P>{t('docs.pipelines.settings.triggerModes.scheduled')}</P>

            <Table
              headers={['Setting', 'Description']}
              rows={[
                ['Enabled', 'Toggle scheduled runs on/off'],
                ['Cron Expression', 'Standard 5-field cron (e.g., */5 * * * * for every 5 minutes)'],
                ['Timezone', 'Timezone for the cron schedule (default: UTC)'],
              ]}
            />

            <CodeBlock title="Cron Examples">
{`*/5 * * * *      Every 5 minutes
0 * * * *        Every hour
0 9 * * 1-5      Weekdays at 9:00 AM
0 0 * * *        Daily at midnight
0 */6 * * *      Every 6 hours`}
            </CodeBlock>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">HTTP Ingestion</h4>
            <P>{t('docs.pipelines.settings.triggerModes.httpIngestion')}</P>

            <Callout type="info">{t('docs.pipelines.settings.triggerModes.httpNote')}</Callout>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Active / Inactive</h4>
            <P>{t('docs.pipelines.settings.triggerModes.activeDesc')}</P>

            <h4 className="font-semibold text-sm text-gray-900 dark:text-white mt-4 mb-2">Debug Mode</h4>
            <P>{t('docs.pipelines.settings.triggerModes.debugDesc')}</P>
          </SubSection>

          {/* Test Events */}
          <SubSection id="test-events" title={t('docs.pipelines.settings.testEvents.title')}>
            <P>{t('docs.pipelines.settings.testEvents.description')}</P>

            <Table
              headers={['Feature', 'Description']}
              rows={[
                ['Create Test Event', 'Define a name and JSON payload to use for testing'],
                ['Auto-Capture', 'Enable debug mode to capture real incoming events as test events'],
                ['Select & Run', 'Choose a test event from the start node and execute the pipeline'],
                ['View Results', 'Inspect input/output payloads at each node after a test run'],
              ]}
            />

            <Callout type="tip">{t('docs.pipelines.settings.testEvents.tip')}</Callout>
          </SubSection>
        </Section>

        {/* ---- Execution & Tracking ---- */}
        <Section id="execution-tracking" title={t('docs.pipelines.execution.title')}>
          <P>{t('docs.pipelines.execution.description')}</P>

          <SubSection id="run-status" title={t('docs.pipelines.execution.runStatus.title')}>
            <Table
              headers={['Status', 'Description']}
              rows={[
                ['pending', 'Run is queued and waiting to start'],
                ['running', 'Pipeline is actively executing'],
                ['success', 'Pipeline completed without errors'],
                ['failed', 'Pipeline encountered an error and stopped'],
              ]}
            />
          </SubSection>

          <SubSection id="node-execution" title={t('docs.pipelines.execution.nodeExec.title')}>
            <P>{t('docs.pipelines.execution.nodeExec.description')}</P>

            <Table
              headers={['Tracked Data', 'Description']}
              rows={[
                ['Status', 'pending, running, success, failed, or skipped'],
                ['Duration', 'Execution time in milliseconds'],
                ['Input Payload', 'Data received by the node'],
                ['Output Payload', 'Data produced by the node'],
                ['Error Details', 'Error message and stack trace (if failed)'],
              ]}
            />
          </SubSection>

          <SubSection id="realtime-updates" title={t('docs.pipelines.execution.realtime.title')}>
            <P>{t('docs.pipelines.execution.realtime.description')}</P>

            <Table
              headers={['Event', 'Description']}
              rows={[
                ['pipeline:run:started', 'Pipeline execution has begun'],
                ['pipeline:run:finished', 'Pipeline completed successfully'],
                ['pipeline:run:failed', 'Pipeline failed with an error'],
                ['pipeline:node:started', 'A node has started executing'],
                ['pipeline:node:finished', 'A node has completed'],
                ['pipeline:node:failed', 'A node has failed'],
              ]}
            />

            <Callout type="info">{t('docs.pipelines.execution.realtime.note')}</Callout>
          </SubSection>
        </Section>

        {/* ---- Versioning ---- */}
        <Section id="versioning" title={t('docs.pipelines.versioning.title')}>
          <P>{t('docs.pipelines.versioning.description')}</P>

          <Table
            headers={['Feature', 'Description']}
            rows={[
              ['Auto-save', 'A new version is created each time you save the pipeline'],
              ['Version History', 'View all previous versions with timestamps'],
              ['Comments', 'Add optional comments when saving a version'],
              ['Full Snapshot', 'Each version stores the complete flow (nodes, edges, variables)'],
              ['Rollback', 'Restore a previous version to undo changes'],
            ]}
          />

          <Callout type="tip">{t('docs.pipelines.versioning.tip')}</Callout>
        </Section>

        {/* ---- API Reference ---- */}
        <Section id="api-reference" title={t('docs.pipelines.api.title')}>
          <P>{t('docs.pipelines.api.description')}</P>

          <SubSection id="pipeline-endpoints" title={t('docs.pipelines.api.pipelines.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/pipelines', 'List all pipelines for the organization'],
                ['POST', '/api/v1/pipelines', 'Create a new pipeline'],
                ['GET', '/api/v1/pipelines/:id', 'Get pipeline details and flow configuration'],
                ['PUT', '/api/v1/pipelines/:id', 'Update pipeline configuration'],
                ['DELETE', '/api/v1/pipelines/:id', 'Delete a pipeline'],
                ['PATCH', '/api/v1/pipelines/:id/activate', 'Activate or deactivate a pipeline'],
              ]}
            />
          </SubSection>

          <SubSection id="execution-endpoints" title={t('docs.pipelines.api.execution.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['POST', '/api/v1/pipelines/:id/test', 'Run a test execution with a test event'],
                ['GET', '/api/v1/pipelines/:id/runs', 'List pipeline execution history'],
                ['GET', '/api/v1/pipelines/:id/runs/:runId', 'Get details of a specific run'],
                ['GET', '/api/v1/pipelines/:id/runs/:runId/nodes', 'Get node-level execution details'],
              ]}
            />
          </SubSection>

          <SubSection id="version-endpoints" title={t('docs.pipelines.api.versions.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/pipelines/:id/versions', 'List all versions of a pipeline'],
                ['POST', '/api/v1/pipelines/:id/versions', 'Save a new version with optional comment'],
              ]}
            />
          </SubSection>

          <SubSection id="test-event-endpoints" title={t('docs.pipelines.api.testEvents.title')}>
            <Table
              headers={['Method', 'Endpoint', 'Description']}
              rows={[
                ['GET', '/api/v1/pipelines/:id/test-events', 'List test events for a pipeline'],
                ['POST', '/api/v1/pipelines/:id/test-events', 'Create a new test event'],
                ['DELETE', '/api/v1/pipelines/:id/test-events/:eventId', 'Delete a test event'],
              ]}
            />
          </SubSection>
        </Section>
      </div>

      {/* Right rail: Table of Contents */}
      <TableOfContents items={tocItems} />
    </div>
  );
}
